import { useEffect, useId, useRef, type ReactNode } from "react";
import { cx } from "./components";

/**
 * Phone navigation sheet: a native modal <dialog> that slides in from the left, so focus stays inside, Escape and the
 * backdrop close it, and the page behind can't scroll. Callers close it when a link inside is followed.
 */
export function NavSheet({ open, onClose, title, children, className }: { open: boolean; onClose: () => void; title: string; children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onClose={onClose}
      onClick={(e) => {
        // A click on the backdrop lands on the <dialog> element itself.
        if (e.target === e.currentTarget) onClose();
      }}
      className={cx(
        "m-0 h-dvh max-h-dvh w-[min(20rem,calc(100vw-3rem))] max-w-none bg-transparent p-3 text-text-primary backdrop:bg-text-primary/40",
        "open:animate-[sheet-in_200ms_cubic-bezier(0.16,1,0.3,1)] motion-reduce:open:animate-none",
        className,
      )}
    >
      <div className="flex h-full flex-col gap-5 overflow-y-auto rounded-card-lg bg-surface-hero p-5 shadow-raised">
        <div className="flex items-center justify-between gap-3">
          <h2 id={titleId} className="text-caption font-bold uppercase tracking-widest">
            {title}
          </h2>
          <button type="button" onClick={onClose} aria-label="Close menu" className="-mr-2 grid size-11 place-items-center rounded-pill hover:bg-surface-background/50">
            <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        {children}
      </div>
    </dialog>
  );
}
